import { FunctionComponent, useEffect, type ChangeEvent } from 'react';
import { useFormContext } from 'react-hook-form';
import { Tooltip } from '@mui/material';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import LoadingButton from '../../../../../ui/LoadingButton';
import type { CreateKidDocumentValues } from '../CreateKidDocumentModal';
import type { KidFiguresRecordModel } from '../../../../../../api/kids/types';
import KidFiguresOverview, {
  KidFiguresStatusBadge,
  resolveKidFiguresStatus,
} from '../KidFiguresOverview';
import {
  numericInputOnChange,
  PERCENTAGE_PLACEHOLDER,
  validateCostField,
  formatKidCostSavedDate,
  formatKidCostSavedTimestamp,
} from '../../utils/kidCostFormUtils';

const StepCostsAndFigures: FunctionComponent<{
  hasCostsInDb: boolean;
  costsLastSavedAt?: string | null;
  hasFiguresInDb: boolean;
  latestFiguresRecord?: KidFiguresRecordModel | null;
  figuresRequireRecalculation?: boolean;
  clientId?: string;
  fundId?: string;
  shareClassId?: string;
  onCreateCostVariables: () => void;
  onCalculateFigures: () => void;
  isCreating: boolean;
  isCalculatingFigures: boolean;
  isLoadingCosts: boolean;
  isLoadingFigures: boolean;
  figuresCalculationError?: string | null;
}> = ({
  hasCostsInDb,
  costsLastSavedAt,
  hasFiguresInDb,
  latestFiguresRecord,
  figuresRequireRecalculation = false,
  clientId,
  fundId,
  shareClassId,
  onCreateCostVariables,
  onCalculateFigures,
  isCreating,
  isCalculatingFigures,
  isLoadingCosts,
  isLoadingFigures,
  figuresCalculationError,
}) => {
  const {
    register,
    formState,
    watch,
    setValue,
    getValues,
    trigger,
  } = useFormContext<CreateKidDocumentValues>();
  const hasPerformanceFee = watch('has_performance_fee');
  const figuresStatus = resolveKidFiguresStatus(
    latestFiguresRecord,
    figuresRequireRecalculation,
    hasFiguresInDb,
  );

  const validatePerformanceFeeField = (
    value: string,
    label: string,
  ): true | string =>
    validateCostField(value, label, getValues('has_performance_fee'));

  const performanceFeeOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const result = numericInputOnChange(e);
    trigger(['appliedPerformanceFee', 'notionalPerformanceFee']);
    return result;
  };

  const clearPerformanceFees = () => {
    setValue('appliedPerformanceFee', '0', {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('notionalPerformanceFee', '0', {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  useEffect(() => {
    trigger(['appliedPerformanceFee', 'notionalPerformanceFee']);
  }, [hasPerformanceFee, trigger]);

  return (
    <div className="space-y-6 p-1">
      <div className="rounded-md border p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-gray-700">Costs</h4>
          {hasCostsInDb && (
            <div className="flex items-center gap-2">
              {costsLastSavedAt && (
                <Tooltip
                  arrow
                  placement="top"
                  title={
                    <div className="text-xs leading-snug">
                      Costs last saved at:{' '}
                      {formatKidCostSavedTimestamp(costsLastSavedAt)}
                    </div>
                  }
                >
                  <div className="flex cursor-default items-center gap-1 text-xs text-gray-500">
                    <span className="text-gray-400">Last saved</span>
                    <span className="font-medium text-gray-700">
                      {formatKidCostSavedDate(costsLastSavedAt)}
                    </span>
                    <HiOutlineInformationCircle className="h-3.5 w-3.5 text-gray-400" />
                  </div>
                </Tooltip>
              )}
              <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                ✓ Costs Saved
              </span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <CostPercentageInput
            name="transactionCost"
            label="Transaction Cost (%)"
            required
          />
          <CostPercentageInput
            name="ongoingCharges"
            label="Ongoing Charges (%)"
            required
          />
          <CostPercentageInput name="entryFee" label="Entry Fee (%)" required />
          <CostPercentageInput name="exitFee" label="Exit Fee (%)" required />
        </div>

        <div className="mt-3 flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-3">
          <input
            type="checkbox"
            id="has_performance_fee"
            {...register('has_performance_fee', {
              onChange: (e) => {
                if (e.target.checked) {
                  setValue('appliedPerformanceFee', '', {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  setValue('notionalPerformanceFee', '', {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                } else {
                  clearPerformanceFees();
                }
              },
            })}
            className="h-4 w-4 rounded border-gray-300 text-mainblue focus:ring-2 focus:ring-mainblue focus:ring-offset-0"
          />
          <label
            htmlFor="has_performance_fee"
            className="cursor-pointer text-sm font-medium text-gray-700"
          >
            Has Performance Fee
          </label>
        </div>

        {hasPerformanceFee && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <CostPercentageInput
              name="appliedPerformanceFee"
              label="Applied Performance Fee (%)"
              required={hasPerformanceFee}
              validate={(value) =>
                validatePerformanceFeeField(value, 'Applied Performance Fee')
              }
              onChange={performanceFeeOnChange}
            />
            <CostPercentageInput
              name="notionalPerformanceFee"
              label="Notional Performance Fee (%)"
              required={hasPerformanceFee}
              validate={(value) =>
                validatePerformanceFeeField(value, 'Notional Performance Fee')
              }
              onChange={performanceFeeOnChange}
            />
          </div>
        )}

        {isLoadingCosts ? (
          <div className="mt-4 text-center text-sm text-gray-500">
            Loading existing costs...
          </div>
        ) : !hasCostsInDb ? (
          <div className="mt-4">
            <LoadingButton
              type="button"
              onClick={onCreateCostVariables}
              isLoading={isCreating}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border bg-mainblue p-2 px-4 text-white transition-colors hover:bg-mainblue/90 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:opacity-50"
            >
              Save Costs
            </LoadingButton>
            <p className="mt-2 text-xs text-gray-500">
              No saved costs were found for this share class.
            </p>
          </div>
        ) : (
          <div className="mt-4">
            <button
              type="button"
              onClick={onCreateCostVariables}
              disabled={isCreating || !formState.isDirty}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border bg-mainblue p-2 px-4 text-white transition-colors hover:bg-mainblue/90 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:opacity-50"
            >
              {isCreating ? 'Updating...' : 'Update Costs'}
            </button>
          </div>
        )}
      </div>

      <div className="rounded-md border p-4">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-700">Figures</h4>
          <KidFiguresStatusBadge status={figuresStatus} />
        </div>
        {hasFiguresInDb && latestFiguresRecord && (
          <div className="overflow-x-auto">
            <KidFiguresOverview
              figuresRecord={latestFiguresRecord}
              clientId={clientId}
              fundId={fundId}
              shareClassId={shareClassId}
            />
          </div>
        )}
        <div className="mt-4">
          <LoadingButton
            type="button"
            onClick={onCalculateFigures}
            isLoading={isCalculatingFigures}
            disabled={isLoadingCosts || !hasCostsInDb || isCalculatingFigures}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg border bg-mainblue p-2 px-4 text-white transition-colors hover:bg-mainblue/90 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:opacity-50"
          >
            {hasFiguresInDb ? 'Recalculate Figures' : 'Calculate Figures'}
          </LoadingButton>
          {isLoadingFigures ? (
            <p className="mt-2 text-xs text-gray-500">
              Checking existing figures...
            </p>
          ) : null}
          {!isLoadingCosts && !hasCostsInDb && (
            <p className="mt-2 text-xs text-gray-500">
              Figure calculation is only available once costs have been saved.
            </p>
          )}
          {hasCostsInDb && figuresStatus === 'none' && (
            <p className="mt-2 text-xs text-amber-700">
              No figures have been calculated yet. Calculate figures to continue.
            </p>
          )}
          {figuresStatus === 'outdated' && (
            <p className="mt-2 text-xs text-amber-700">
              Figures are out of date (older than the latest reporting Friday).
              Recalculate to continue.
            </p>
          )}
          {figuresCalculationError && (
            <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {figuresCalculationError}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type CostFieldName =
  | 'entryFee'
  | 'exitFee'
  | 'ongoingCharges'
  | 'transactionCost'
  | 'appliedPerformanceFee'
  | 'notionalPerformanceFee';

const CostPercentageInput: FunctionComponent<{
  name: CostFieldName;
  label: string;
  required?: boolean;
  validate?: (value: string) => true | string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => string | void;
}> = ({ name, label, required = false, validate, onChange }) => {
  const { register, formState, getFieldState } =
    useFormContext<CreateKidDocumentValues>();
  const { error, isDirty } = getFieldState(name, formState);
  const showSuccess = isDirty && !error;

  return (
    <div>
      <label
        className={
          'mb-1 block text-xs font-medium ' +
          (error
            ? 'text-red-700'
            : showSuccess
              ? 'text-green-700'
              : 'text-gray-700')
        }
      >
        {label}
      </label>
      <input
        type="text"
        inputMode="decimal"
        placeholder={PERCENTAGE_PLACEHOLDER}
        className={
          'w-full rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 ' +
          (error
            ? 'border border-red-500 focus:ring-red-300'
            : showSuccess
              ? 'border border-green-500 focus:ring-green-500'
              : 'border border-gray-300 focus:ring-blue-300')
        }
        aria-invalid={Boolean(error)}
        {...register(name, {
          required: required ? 'Required' : false,
          validate:
            validate ??
            ((value) => validateCostField(value, label, required)),
          onChange: (e) => {
            const result = onChange?.(e) ?? numericInputOnChange(e);
            return result;
          },
        })}
      />
      {error && (
        <p className="mt-1 text-xs text-red-600">{error.message as string}</p>
      )}
    </div>
  );
};

export default StepCostsAndFigures;
